import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // We intentionally do not surface the backend's per-email details —
      // the backend always returns 200 regardless of whether the account
      // exists so we cannot leak that either.
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
      });
    } catch (_) {
      // Even a network failure is presented as success to avoid enumeration.
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  return (
    <div className="min-h-screen shine-gradient-bg flex items-center justify-center px-4 py-10">
      <Card className="shine-card w-full max-w-md p-6 sm:p-8 rounded-2xl shadow-2xl">
        <div className="mb-6"><Logo size="md" /></div>
        <h2 className="text-2xl font-bold text-white">Forgot password?</h2>
        <p className="text-sm text-slate-400 mt-1">
          Enter your email and we&apos;ll send you a link to reset it.
        </p>

        {done ? (
          <div className="mt-6 space-y-4" data-testid="forgot-password-success">
            <div className="flex items-start gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4">
              <CheckCircle2 className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-100 leading-relaxed">
                If <span className="font-semibold">{email}</span> is a registered account,
                we&apos;ve just sent a reset link. The link expires in 1 hour.
              </div>
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="w-full font-semibold text-white"
              style={{ background: 'linear-gradient(180deg,#4ec178,#2da655)' }}
              data-testid="forgot-back-to-login"
            >
              Back to sign in
            </Button>
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="email" className="text-slate-200">Email</Label>
              <Input
                id="email"
                data-testid="forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-[#0f2240] border-amber-500/20 text-white placeholder:text-slate-500 focus-visible:ring-amber-400"
                placeholder="you@shineadagency.com"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full font-semibold text-white"
              style={{ background: 'linear-gradient(180deg,#4ec178,#2da655)' }}
              data-testid="forgot-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
            </Button>
            <Link
              to="/login"
              className="flex items-center gap-1.5 justify-center text-xs text-amber-300 hover:text-amber-200 pt-2"
              data-testid="forgot-back-link"
            >
              <ArrowLeft className="h-3 w-3" /> Back to sign in
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}
